import { AmsAuditApproval } from '../../../services/amsAuditApprovalService';

export interface AuditPendingSummary {
  pendingSectionLabels: string[];
  totalPendingCheckpoints: number;
  rejectedSectionLabels: string[];
  totalRejectedCheckpoints: number;
}

export const computeAuditPendingSummary = (approval: AmsAuditApproval): AuditPendingSummary => {
  const pendingSectionLabels = [
    approval.sections.auditType.status === 'pending' ? 'Audit Type' : null,
    approval.sections.auditors.status === 'pending' ? 'Auditors' : null,
    approval.sections.assetsAndEquipment.status === 'pending' ? 'Assets & Equipment' : null,
  ].filter((label): label is string => label !== null);

  const rejectedSectionLabels = [
    approval.sections.auditType.status === 'rejected' ? 'Audit Type' : null,
    approval.sections.auditors.status === 'rejected' ? 'Auditors' : null,
    approval.sections.assetsAndEquipment.status === 'rejected' ? 'Assets & Equipment' : null,
  ].filter((label): label is string => label !== null);

  const totalPendingCheckpoints =
    approval.checkpointApprovals.projectCheckpoints.filter((cp) => cp.status === 'pending').length +
    approval.checkpointApprovals.assetCheckpoints.filter((cp) => cp.status === 'pending').length;

  const totalRejectedCheckpoints =
    approval.checkpointApprovals.projectCheckpoints.filter((cp) => cp.status === 'rejected').length +
    approval.checkpointApprovals.assetCheckpoints.filter((cp) => cp.status === 'rejected').length;

  return { pendingSectionLabels, totalPendingCheckpoints, rejectedSectionLabels, totalRejectedCheckpoints };
};

export const summarizeAuditPendingState = (approval: AmsAuditApproval): string => {
  if (approval.overallStatus === 'draft') return 'Not yet submitted';
  if (approval.overallStatus === 'rejected') return 'Rejected — see comments';

  const { pendingSectionLabels, totalPendingCheckpoints } = computeAuditPendingSummary(approval);
  if (pendingSectionLabels.length > 0 || totalPendingCheckpoints > 0) {
    return `${pendingSectionLabels.length} section(s), ${totalPendingCheckpoints} checkpoint(s) pending`;
  }
  return 'All sections approved';
};

/**
 * A richer, approver-facing one-liner naming the specific sections/checkpoints
 * that are pending or rejected — used as the audit row sub-line on the approvals page.
 */
export const describeAuditForApprover = (approval: AmsAuditApproval): string => {
  const { pendingSectionLabels, totalPendingCheckpoints, rejectedSectionLabels, totalRejectedCheckpoints } =
    computeAuditPendingSummary(approval);

  if (approval.overallStatus === 'rejected') {
    const parts: string[] = [];
    if (rejectedSectionLabels.length > 0) parts.push(rejectedSectionLabels.join(', '));
    if (totalRejectedCheckpoints > 0) parts.push(`${totalRejectedCheckpoints} checkpoint(s) rejected`);
    return parts.length > 0 ? parts.join(' · ') : 'Rejected — see comments';
  }
  if (approval.overallStatus === 'approved_to_start') return 'All sections approved · ready to start';

  const parts: string[] = [];
  if (pendingSectionLabels.length > 0) parts.push(`${pendingSectionLabels.join(', ')} pending`);
  if (totalPendingCheckpoints > 0) parts.push(`${totalPendingCheckpoints} checkpoint(s) pending`);
  return parts.length > 0 ? parts.join(' · ') : 'Awaiting review';
};
